import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Icon } from "@iconify/react";

// ** Components
import Datagrid from "@/components/tables/DataGrid.tsx";
import AppModal from "@/components/modal/AppModal.tsx";
import { StudioForm } from "@/views/admin/studios/StudioForm.tsx";
import DeleteModal from "@/components/modal/DeleteModal.tsx";
import AppCard from "@/components/AppCard.tsx";

// ** Hooks
import {
  useGetStudiosQuery,
  useCreateStudioMutation,
  useUpdateStudioMutation,
} from "@/api/studiosApi.ts";

// ** Utils
import { mutationFeedback } from "@/services/utils.tsx";

// ** Schema
import type { StudioFormType } from "@/schemas/studio.ts";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

// ** Constants
import { STUDIO_STATUS_COLORS } from "@/constants";

type Studio = components["schemas"]["Studio.jsonld-studio.read_read"];

export const Route = createFileRoute("/admin/studios")({
  component: StudiosPage,
});

function StudiosPage() {
  const { t } = useTranslation();

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");

  // API mutations
  const [createStudio, { isLoading: isCreating }] = useCreateStudioMutation();
  const [updateStudio, { isLoading: isUpdating }] = useUpdateStudioMutation();
  const isSubmitting = isCreating || isUpdating;

  const handleAdd = () => {
    setSelectedStudio(null);
    setFormOpen(true);
  };

  const handleEdit = (studio: Studio) => {
    setSelectedStudio(studio);
    setFormOpen(true);
  };

  const handleDelete = (studio: Studio) => {
    setSelectedStudio(studio);
    setDeleteOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedStudio(null);
  };

  const handleStudioSubmit = async (data: StudioFormType) => {
    const payload = {
      ...data,
      studioModel: data.studioModel ?? null,
      availableFrom: data.status === "available_from" ? data.availableFrom : null,
      // Convertit "" → null pour que PATCH puisse vider un champ précédemment
      // renseigné (cf. AC9 story 8.3, leçon story 8.2 sur null vs undefined).
      descriptionFr: data.descriptionFr?.trim() ? data.descriptionFr.trim() : null,
      descriptionEn: data.descriptionEn?.trim() ? data.descriptionEn.trim() : null,
      equipmentsFr: data.equipmentsFr?.trim() ? data.equipmentsFr.trim() : null,
      equipmentsEn: data.equipmentsEn?.trim() ? data.equipmentsEn.trim() : null,
    };

    let result;
    if (selectedStudio?.uuid) {
      result = await updateStudio({ uuid: selectedStudio.uuid, data: payload });
    } else {
      result = await createStudio(payload);
    }

    mutationFeedback(result, {
      success: selectedStudio
        ? t("admin.studios.messages.updated")
        : t("admin.studios.messages.created"),
      error: selectedStudio
        ? t("admin.studios.messages.updateError")
        : t("admin.studios.messages.createError"),
    });

    if (!("error" in result)) {
      handleFormClose();
    }
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedStudio(null);
  };

  const columns: GridColDef<Studio>[] = [
    {
      field: "name",
      headerName: t("admin.studios.columns.name"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "studioModel",
      headerName: t("admin.studios.columns.model"),
      width: 150,
      valueGetter: (value: Studio["studioModel"]) => value?.name ?? "—",
    },
    {
      field: "building",
      headerName: t("admin.studios.columns.building"),
      width: 100,
    },
    {
      field: "floorLabel",
      headerName: t("admin.studios.columns.floor"),
      width: 100,
    },
    {
      field: "size",
      headerName: t("admin.studios.columns.surface"),
      width: 100,
    },
    {
      field: "price",
      headerName: t("admin.studios.columns.price"),
      width: 100,
      valueFormatter: (value: string) => `${value}€`,
    },
    {
      field: "status",
      headerName: t("admin.studios.columns.status"),
      width: 140,
      renderCell: (params: GridRenderCellParams<Studio>) => {
        const status = params.row.status;
        const label =
          status === "available_from" && params.row.availableFrom
            ? `${t("status.available_from_short")} ${new Date(
                params.row.availableFrom
              ).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
              })}`
            : t(`status.${status}`);

        return (
          <Chip
            label={label}
            color={STUDIO_STATUS_COLORS[status] ?? "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "specificContent",
      headerName: t("admin.studios.columns.specificContent"),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Studio>) => {
        const studio = params.row;
        const hasText =
          Boolean(studio.descriptionFr?.trim()) ||
          Boolean(studio.descriptionEn?.trim()) ||
          Boolean(studio.equipmentsFr?.trim()) ||
          Boolean(studio.equipmentsEn?.trim());
        const photosCount = studio.medias?.length ?? 0;

        return (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Tooltip
              title={
                hasText
                  ? t("admin.studios.specific.hasContent")
                  : t("admin.studios.specific.noContent")
              }
            >
              <Chip
                size="small"
                variant={hasText ? "filled" : "outlined"}
                color={hasText ? "primary" : "default"}
                icon={
                  <Icon
                    icon={hasText ? "tabler:file-text" : "tabler:file-off"}
                    width={14}
                  />
                }
                label={
                  hasText
                    ? t("admin.studios.specific.hasContent")
                    : t("admin.studios.specific.noContent")
                }
              />
            </Tooltip>
            <Tooltip
              title={
                photosCount > 0
                  ? t("admin.studios.specific.photosCount", { count: photosCount })
                  : t("admin.studios.specific.noPhotos")
              }
            >
              <Chip
                size="small"
                variant={photosCount > 0 ? "filled" : "outlined"}
                color={photosCount > 0 ? "primary" : "default"}
                icon={<Icon icon="tabler:photo" width={14} />}
                label={photosCount}
              />
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: t("admin.common.actions"),
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Studio>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t("admin.common.edit")}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <Icon icon="tabler:edit" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("admin.common.delete")}>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <Icon icon="tabler:trash" width={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Build query params with filters
  const queryParams: Record<string, unknown> = {};
  if (statusFilter) queryParams.status = statusFilter;
  if (buildingFilter) queryParams.building = buildingFilter;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.studios.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon icon="tabler:plus" />}
          onClick={handleAdd}
        >
          {t("admin.studios.addStudio")}
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("admin.studios.filters.status")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("admin.studios.filters.status")}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t("admin.common.all")}</MenuItem>
            <MenuItem value="available">{t("status.available")}</MenuItem>
            <MenuItem value="available_from">
              {t("status.available_from_label")}
            </MenuItem>
            <MenuItem value="reserved">{t("status.reserved")}</MenuItem>
            <MenuItem value="occupied">{t("status.occupied")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("admin.studios.filters.building")}</InputLabel>
          <Select
            value={buildingFilter}
            label={t("admin.studios.filters.building")}
            onChange={(e) => setBuildingFilter(e.target.value)}
          >
            <MenuItem value="">{t("admin.common.all")}</MenuItem>
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="B">B</MenuItem>
            <MenuItem value="C">C</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* DataGrid */}
      <AppCard>
        <Datagrid
          columns={columns}
          queryFunction={useGetStudiosQuery}
          queryFunctionParam={queryParams}
          defaultOrder="number"
          searchField="search"
        />
      </AppCard>

      {/* Form Modal - key forces remount when switching between create/edit */}
      <AppModal
        key={selectedStudio?.uuid ?? "create"}
        open={formOpen}
        onClose={handleFormClose}
        title={selectedStudio ? t("admin.studios.editStudio") : t("admin.studios.addStudio")}
        sx={{ width: 600, maxHeight: "90vh", overflow: "auto" }}
      >
        <StudioForm
          studio={selectedStudio}
          onSubmit={handleStudioSubmit}
          onCancel={handleFormClose}
          isSubmitting={isSubmitting}
        />
      </AppModal>

      {/* Delete Modal */}
      {selectedStudio && (
        <DeleteModal
          open={deleteOpen}
          onClose={handleDeleteClose}
          elementLabel={selectedStudio.name ?? `Studio ${selectedStudio.number}`}
          elementIri={`/api/studios/${selectedStudio.uuid}`}
          onSuccess={handleDeleteClose}
        />
      )}
    </Box>
  );
}
